/**
 * O MODAL DO ADMIN ATUAL, sobre o `Modal` do Chassi: X, ESC e clique fora fecham, e o papel
 * "Apenas Consulta" perde os botões, como no legado.
 *
 * POR QUE A EXCEÇÃO DO LINT MORA AQUI: a plataforma proíbe preenchimento de dado em modal, e o
 * legado abre em modal o "Registrar pagamento", o "Atenção: valor diferente", o "Relatório PDF", o
 * compromisso e a confirmação (`admin.html:2906`, `2962`, `2994`, `2979`). A regra desta migração é
 * o funcionamento IDÊNTICO, e trocar modal por rota é mudança de gesto, que é decisão do fundador.
 * A exceção fica neste UM lugar, com o motivo escrito, e nenhuma tela precisa repeti-la.
 *
 * TRÊS COISAS QUE O `Modal` DO CHASSI NÃO FAZ, e este arquivo faz sem editar `packages/`:
 *   1. CLIQUE FORA: o `<dialog>` do Chassi fecha com o X e com o ESC, e não com o clique no fundo.
 *      O clique no fundo de um `<dialog>` aberto chega com o PRÓPRIO dialog como alvo (o fundo é
 *      um pseudo-elemento dele, e o conteúdo não tem respiro próprio), então basta reconhecer o
 *      dialog deste modal pela marca escondida que vai dentro dele.
 *   2. MODAL QUE NÃO FECHA: o de confirmação de compromisso do legado não fecha por nada
 *      (`admin.html:7719`); `fechavel={false}` tira o X, o clique fora e o ESC.
 *   3. O ACHADO 5 DO INVENTÁRIO (`docs/PARIDADE-ADMIN.md`, seção 4): para "Apenas Consulta" o
 *      legado esconde todo botão que não é de navegação (`admin.html:3395`), inclusive o X e os
 *      botões do rodapé do modal, e a pessoa só sai por ESC ou pelo clique fora. O fundador decidiu
 *      em 19/09/2026 que os defeitos do legado são REPRODUZIDOS; a reprodução está isolada na
 *      constante `ACHADO_5_CONSULTA_SEM_BOTOES`, e corrigir é trocar uma linha.
 *
 * Dona desta peça: a frente A5 (Financeiro e Sistema), por designação do principal em 19/09/2026.
 */

// Efeito, identificador único e referência do React.
import { useEffect, useId, useRef, type ReactNode } from 'react';
// O modal do Chassi, que desenha o cabeçalho, o X e o rodapé.
import { Modal } from '@complexity/ui';
// O papel de quem está logado, para o achado 5.
import { usePainel } from '../contexto.js';

/**
 * ACHADO 5, reproduzido por decisão do fundador: `true` esconde o X e o rodapé para "Apenas
 * Consulta". Trocar para `false` corrige o legado, e é a única linha a mexer.
 */
const ACHADO_5_CONSULTA_SEM_BOTOES = true;

export interface ModalLegadoProps {
  readonly aberto: boolean;
  // Chamado no X, no ESC e no clique fora; a tela decide o que "fechar" significa ali.
  readonly aoFechar: () => void;
  readonly titulo: string;
  readonly children: ReactNode;
  // Os botões do pé, na ordem do legado: o secundário primeiro, a ação principal por último.
  readonly rodape?: ReactNode;
  // `false` para o modal que o legado não deixa fechar (sem X, sem ESC, sem clique fora).
  readonly fechavel?: boolean;
  // `false` para o modal que fecha por X e ESC, mas NÃO pelo clique fora.
  readonly fechaAoClicarFora?: boolean;
  // Identificador para a suíte achar este modal na tela.
  readonly testid?: string;
}

export function ModalLegado({
  aberto,
  aoFechar,
  titulo,
  children,
  rodape,
  fechavel = true,
  fechaAoClicarFora = true,
  testid,
}: ModalLegadoProps) {
  const { acesso } = usePainel();
  // A marca única deste modal: dois modais abertos ao mesmo tempo (a confirmação por cima do
  // pagamento, por exemplo) não podem confundir o clique fora de um com o do outro.
  const marca = useId();
  // A função de fechar mais recente, lida pelos ouvintes sem recriá-los a cada render.
  const fechar = useRef(aoFechar);
  fechar.current = aoFechar;
  // Achado 5: consulta não vê botão nenhum dentro do modal.
  const semBotoes = ACHADO_5_CONSULTA_SEM_BOTOES && acesso.papel === 'consulta';

  useEffect(() => {
    if (!aberto) return;
    // O `<dialog>` deste modal, achado pela marca que vai dentro dele.
    const dialogo = document.querySelector(`[data-modal-legado="${CSS.escape(marca)}"]`)?.closest('dialog');
    if (!dialogo) return;
    // O X do cabeçalho do Chassi, pelo nome acessível que ele declara.
    const botaoX = dialogo.querySelector<HTMLButtonElement>('header button[aria-label="Fechar"]');
    // Sem X quando o modal não fecha, e para consulta (achado 5).
    if (botaoX) botaoX.hidden = !fechavel || semBotoes;
    // O ESC dispara `cancel` no dialog; cancelar o evento impede o fechamento nativo.
    const aoCancelar = (evento: Event) => {
      if (!fechavel) evento.preventDefault();
    };
    // O clique no fundo chega com o próprio dialog como alvo.
    const aoClicar = (evento: MouseEvent) => {
      if (evento.target === dialogo && fechavel && fechaAoClicarFora) fechar.current();
    };
    dialogo.addEventListener('cancel', aoCancelar);
    dialogo.addEventListener('click', aoClicar);
    return () => {
      dialogo.removeEventListener('cancel', aoCancelar);
      dialogo.removeEventListener('click', aoClicar);
    };
  }, [aberto, marca, fechavel, fechaAoClicarFora, semBotoes]);

  // FECHADO NÃO EXISTE NO DOCUMENTO: o `Modal` do Chassi fixa `id="cx-modal-titulo"` no título, e
  // com vários modais montados na mesma tela o `aria-labelledby` de todos aponta para o PRIMEIRO, e
  // o leitor de tela anuncia o título errado. Montando só o aberto, sobra um título por modal visível.
  if (!aberto) return null;

  return (
    // eslint-disable-next-line no-restricted-syntax -- migração idêntica ao legado, que abre estes modais (admin.html:2906, 2962, 2979, 2994); trocar por rota é decisão do fundador
    <Modal aberto={aberto} aoFechar={aoFechar} titulo={titulo} rodape={semBotoes ? undefined : rodape}>
      {/* A marca que os ouvintes procuram; escondida da tela e do leitor de tela. */}
      <span hidden data-modal-legado={marca} data-testid={testid} />
      {children}
    </Modal>
  );
}
